import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useHistory } from '@useHistory/useHistoryCompat';
import { EntityUIState, EntityUIStateWrapper } from '../../../core/interfaces/entitiy-ui-state';
import RetryApiAlert from '../../../fallback-screens/components/retry-api-alert/RetryApiAlert';
import { HdResolvedComponent } from '../../../routing/components/resolved-component/HdResolvedComponent';
import { HdAuthRequestType } from '../../interfaces/auth';
import { RecoveryCodesFactory } from '../../factories/recoveryCodesFactory';
import TFACodeList from '../tfa-code-list/TfaCodeList';
import { downloadCodes, getDownloadCodeContent } from '../../utils/recoveryCodes';
import { HdLink } from '../../../components';
import HdButton from '../../../button/components/button/HdButton';
import useClipboardService from '../../../clipboard/hooks/useClipboardService';
import { getDataIdGenerator } from '../../../core/utils/generateDataId';
import AccountsAPI from '../../../account/api/AccountsAPI';
import { HdAuth } from '../auth-layout/AuthLayout';
import { FeaturedCustomers } from '../testimonial-section/TestimonialSection';
import { useFeaturedCustomers } from '../../hooks/useFeaturedCustomer';
import { useLoginGuard } from '../../hooks/useLoginGuard';

export interface RecoveryCodesPageProps {
  featuredCustomers: FeaturedCustomers;
}

export function RecoveryCodesPage() {
  const { getFeaturedCustomers } = useFeaturedCustomers();
  const { executeLoginGuard } = useLoginGuard();

  return (
    <HdResolvedComponent
      Component={RecoveryCodesPageInner}
      resolve={{
        featuredCustomers: getFeaturedCustomers
      }}
      canActivate={[executeLoginGuard]}
    />
  );
}

export function RecoveryCodesPageInner({ featuredCustomers }: RecoveryCodesPageProps) {
  const [codeList, setCodeList] = useState<RecoveryCode[]>([]);
  const [uiState, setUiState] = useState<EntityUIStateWrapper>({ state: EntityUIState.LOADING });

  const clipboardService = useClipboardService();
  const history = useHistory();
  const { search } = useLocation();

  const errored = uiState.state === EntityUIState.ERRORED;
  const loading = uiState.state === EntityUIState.LOADING;

  const nextURL = new URLSearchParams(search).get('next');

  useEffect(() => {
    getRecoveryCodes();
  }, []);

  const getRecoveryCodes = () => {
    setUiState({ state: EntityUIState.LOADING });
    AccountsAPI.getTfaRecoveryCodes()
      .then(({ data }) => {
        setUiState({ state: EntityUIState.IDLE });
        setCodeList(RecoveryCodesFactory(data.recovery_codes));
      })
      .catch(error => {
        setUiState({ state: EntityUIState.ERRORED, error });
      });
  };

  const copyToClipboard = () => {
    clipboardService.copy(getDownloadCodeContent(codeList), 'Recovery codes copied to clipboard');
  };

  const navigateToDashboard = () => {
    history.push(nextURL || '/');
  };

  const dataIdGenerator = getDataIdGenerator('recovery-codes-page');

  return (
    <HdAuth
      selectedAuthType={HdAuthRequestType.TFA_RECOVERY_CODES}
      featuredCustomers={featuredCustomers}
      loginContainerStyles={{ width: '600px' }}
    >
      {!errored ? (
        <>
          <TFACodeList uiState={uiState} codes={codeList} />

          <div className='mt-8 text-center'>
            <HdButton
              dataId={dataIdGenerator('copy')}
              icon='copy'
              disabled={loading}
              onClick={copyToClipboard}
            >
              Copy
            </HdButton>

            <HdButton
              dataId={dataIdGenerator('download')}
              icon='download'
              disabled={loading}
              className='ml-3'
              onClick={() => downloadCodes(codeList)}
            >
              Download
            </HdButton>
          </div>
        </>
      ) : (
        <RetryApiAlert
          dataId={dataIdGenerator('')}
          actionHandler={getRecoveryCodes}
          error={uiState.error}
        />
      )}

      <HdLink
        dataId={dataIdGenerator('goto-dashboard')}
        tag='button'
        icon='right'
        className='mt-4'
        onClick={navigateToDashboard}
      >
        Go to Dashboard
      </HdLink>
    </HdAuth>
  );
}
